import { useState, useEffect, createContext, useContext, useRef, memo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  LayoutDashboard, 
  CalendarRange,
  FolderOpen, 
  Users, 
  Image, 
  LogOut,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  Activity,
  Ticket,
  Globe,
  Crown,
  UserCircle,
  Lock,
  CalendarClock,
  Sparkles,
  Menu,
  X,
  MessageSquare,
  Mail,
  Store,
  Gift,
  ExternalLink,
  Building2,
  FileText,
  Disc3,
  Scale,
  Star,
  Newspaper,
  Settings,
  AlertTriangle,
  DollarSign,
  Megaphone, ShoppingBag
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_event-platform-39/artifacts/no11df8z_Blanco-Icono.png";

// Context for sidebar state
const SidebarContext = createContext(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isCollapsed: false, setIsCollapsed: () => {} };
  }
  return context;
};

export const SidebarProvider = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('admin_sidebar_collapsed');
    return saved === 'true';
  });
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem('admin_sidebar_collapsed', isCollapsed);
  }, [isCollapsed]);

  return (
    <SidebarContext.Provider value={{ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }}>
      {children}
    </SidebarContext.Provider>
  );
};

// Navigation groups configuration
const navigationGroups = [
  {
    title: 'Overview',
    items: [
      { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', permission: 'dashboard.view' },
    ]
  },
  {
    title: 'Contenido Público',
    items: [
      { path: '/admin/eventos', icon: CalendarRange, label: 'Eventos', permission: 'events.view' },
      { path: '/admin/eventos-destacados', icon: Star, label: 'Banner Destacados', permission: 'events.view' },
      { path: '/admin/galeria', icon: Image, label: 'Galería', permission: 'gallery.view' },
      { path: '/admin/suscripciones', icon: Crown, label: 'FULL BRAVX', permission: 'subscriptions.view' },
      { path: '/admin/merch', icon: ShoppingBag, label: 'Merch', permission: 'events.view' },
      { path: '/admin/bravx', icon: Sparkles, label: 'BRAVX Points', permission: 'settings.view' },
      { path: '/admin/auras', icon: Sparkles, label: 'Auras', permission: 'auras.view' },
      { path: '/admin/usuarios-publicos', icon: UserCircle, label: 'Usuarios Públicos', permission: 'public_users.view' },
      { path: '/admin/dj-applications', icon: Disc3, label: 'Solicitudes DJ', permission: 'dj_requests_view' },
      { path: '/admin/convocatorias', icon: Disc3, label: 'Convocatorias DJ', permission: 'dj_requests_view' },
      { path: '/admin/partners', icon: Store, label: 'Partners', permission: 'partners.view' },
      { path: '/admin/aprobar-beneficios', icon: Gift, label: 'Aprobar Beneficios', permission: 'partners.view' },
      { path: '/admin/productoras', icon: Building2, label: 'Productoras', permission: 'producers.view' },
      { path: '/admin/prensa', icon: Newspaper, label: 'Servicios', permission: 'press.view' },
      { path: '/admin/contratos', icon: FileText, label: 'Contratos', permission: 'press.view' },
      { path: '/admin/liquidaciones', icon: DollarSign, label: 'Liquidaciones', permission: 'settlements.view' },
      { path: '/admin/strikes', icon: AlertTriangle, label: 'Moderación', permission: 'press.view' },
      { path: '/admin/plataforma', icon: Settings, label: 'Plataforma', permission: 'settings.view' },
      { path: '/admin/formularios', icon: FileText, label: 'Formularios', permission: 'settings.view' },
      { path: '/admin/marketing', icon: Mail, label: 'Marketing', permission: 'marketing.view' },
      { path: '/admin/campanas/nueva', icon: Megaphone, label: 'Crear Campaña', permission: 'marketing.send' },
      { path: '/admin/campaign-credits', icon: Gift, label: 'Créditos Campañas', permission: 'settings.view' },
      { path: '/admin/quienes-somos', icon: Users, label: 'Quiénes Somos', permission: 'about.view' },
      { path: '/admin/configuracion', icon: Globe, label: 'Config. Sitio', permission: 'settings.view' },
    ]
  },
  {
    title: 'Gestión Interna',
    items: [
      { path: '/admin/calendario', icon: CalendarClock, label: 'Calendario', permission: 'calendar.view' },
      { path: '/admin/tickets', icon: Ticket, label: 'Tickets', permission: 'tickets.view' },
      { path: '/admin/archivos', icon: FolderOpen, label: 'Archivos', permission: 'files.view' },
      { path: '/admin/notas', icon: MessageSquare, label: 'Notas & Chat', permission: 'notes.view' },
      { path: '/admin/arrepentimiento', icon: Scale, label: 'Arrepentimiento', permission: 'settings.view' },
      { path: '/admin/url-404', icon: AlertTriangle, label: 'URLs Rotas', permission: 'settings.view' },
      { path: '/admin/logs', icon: Activity, label: 'Actividad', ownerOnly: true },
    ]
  },
  {
    title: 'Sistema',
    ownerOnly: true,
    items: [
      { path: '/admin/usuarios', icon: ShieldCheck, label: 'Admin Users', permission: 'users.view' },
      { path: '/admin/roles', icon: Lock, label: 'Roles', ownerOnly: true },
    ]
  },
  {
    title: 'Cuenta',
    items: [
      { path: '/admin/mi-perfil', icon: UserCircle, label: 'Mi Perfil', permission: null },
    ]
  }
];

// NavItem component - Memoized
const NavItem = memo(({ item, isActive, isCollapsed, onClick, notificationCount, disabled = false }) => {
  const Icon = item.icon;
  const active = isActive && !disabled;
  // Only show notification if count is a valid number greater than 0 and not disabled
  const showNotification = !disabled && typeof notificationCount === 'number' && notificationCount > 0;

  // If disabled, render as a div instead of a Link
  const Component = disabled ? 'div' : Link;
  const componentProps = disabled 
    ? { 
        'data-testid': `sidebar-${item.path.replace('/admin/', '').replace('/', '-') || 'dashboard'}-disabled`,
        title: 'No tienes permiso para acceder a esta sección'
      }
    : { 
        to: item.path, 
        onClick,
        'data-testid': `sidebar-${item.path.replace('/admin/', '').replace('/', '-') || 'dashboard'}`
      };

  return (
    <Component
      {...componentProps}
      className={`
        group relative flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200
        ${disabled 
          ? 'grayscale opacity-40 cursor-not-allowed pointer-events-none' 
          : active 
            ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
            : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
        }
        ${isCollapsed ? 'justify-center' : ''}
      `}
    >
      {/* Active indicator bar */}
      {active && !disabled && (
        <motion.div 
          layoutId="activeIndicator"
          className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 bg-[#D4AF37] rounded-r-full"
          initial={false}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        />
      )}
      
      {/* Icon with notification dot */}
      <div className="relative flex-shrink-0">
        <Icon 
          size={20} 
          className={`transition-colors ${active && !disabled ? 'text-[#D4AF37]' : disabled ? '' : 'group-hover:text-[#D4AF37]'}`} 
        />
        {showNotification && (
          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        )}
      </div>
      
      {!isCollapsed && (
        <>
          <span className={`text-sm font-medium whitespace-nowrap flex-1 ${active && !disabled ? 'text-[#D4AF37]' : ''}`}>
            {item.label}
          </span>
          {showNotification && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full min-w-[18px] text-center">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
          {disabled && (
            <Lock size={12} className="text-[#52525B]" />
          )}
        </>
      )}
    </Component>
  );
});

NavItem.displayName = 'NavItem';

// SidebarContent component - Extracted
const SidebarContent = memo(({ 
  isCollapsed, 
  setIsCollapsed, 
  setIsMobileOpen, 
  user, 
  isOwner, 
  hasPermission, 
  handleLogout, 
  isActive,
  saveScrollPosition,
  navRef,
  notifications
}) => {
  const canViewItem = (item) => {
    if (item.ownerOnly) return isOwner;
    if (item.permission) return hasPermission(item.permission) || isOwner;
    return true;
  };

  const canViewGroup = (group) => {
    if (group.ownerOnly && !isOwner) return false;
    return group.items.some(item => canViewItem(item));
  };

  const handleNavClick = () => {
    saveScrollPosition();
    setIsMobileOpen(false);
  };
  
  // Get notification count for a specific path
  const getNotificationCount = (path) => {
    if (!notifications) return 0;
    switch (path) {
      case '/admin/eventos':
        return notifications.pending_events || 0;
      case '/admin/aprobar-beneficios':
        return notifications.pending_benefits || 0;
      case '/admin/productoras':
        return notifications.pending_producers || 0;
      case '/admin/partners':
        return notifications.pending_partners || 0;
      case '/admin/dj-applications':
        return notifications.pending_dj || 0;
      case '/admin/prensa':
        return notifications.pending_press || 0;
      case '/admin/contratos':
        return notifications.pending_quotes || 0;
      case '/admin/strikes':
        return (notifications.pending_strikes || 0) + (notifications.pending_claims || 0);
      case '/admin/arrepentimiento':
        return notifications.pending_cancellations || 0;
      default:
        return 0;
    }
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-[#0A0A0A] to-[#080808]">
      {/* Logo Header */}
      <div className={`p-4 border-b border-[#1A1A1A] flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
        <Link to="/admin" className="flex items-center gap-3 group" onClick={() => setIsMobileOpen(false)}>
          <div className="relative">
            <div className="absolute inset-0 bg-[#D4AF37]/20 blur-lg rounded-full opacity-0 group-hover:opacity-100 transition-opacity" />
            <img src={LOGO_URL} alt="Bravo" className="h-10 w-10 object-contain relative z-10" />
          </div>
          {!isCollapsed && (
            <div>
              <span className="font-black text-lg tracking-tight whitespace-nowrap bg-gradient-to-r from-white to-zinc-400 bg-clip-text text-transparent">BRAVO</span>
              <span className="text-[10px] text-[#D4AF37] block -mt-0.5 font-mono tracking-widest">ADMIN</span>
            </div>
          )}
        </Link>
        
        {/* Collapse button - Desktop only */}
        {!isCollapsed && (
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            data-testid="sidebar-collapse-btn"
            className="hidden lg:flex p-1.5 rounded-md hover:bg-[#D4AF37]/10 text-[#52525B] hover:text-[#D4AF37] transition-all"
          >
            <ChevronLeft size={18} />
          </button>
        )}
      </div>

      {/* Expand button when collapsed */}
      {isCollapsed && (
        <button
          onClick={() => setIsCollapsed(false)}
          data-testid="sidebar-expand-btn"
          className="hidden lg:flex mx-auto mt-4 p-1.5 rounded-md hover:bg-[#D4AF37]/10 text-[#52525B] hover:text-[#D4AF37] transition-all"
        >
          <ChevronRight size={18} />
        </button>
      )}

      {/* Navigation */}
      <nav 
        ref={navRef}
        className="flex-1 overflow-y-auto p-3 space-y-6 scrollbar-thin scrollbar-thumb-zinc-800 scrollbar-track-transparent" 
        id="admin-sidebar-nav"
      >
        {navigationGroups.map((group) => {
          // Show group if it has at least one item (regardless of permissions for visual display)
          if (group.ownerOnly && !isOwner) return null;
          if (group.items.length === 0) return null;

          return (
            <div key={group.title}>
              {!isCollapsed && (
                <h3 className="text-[10px] font-bold text-[#3F3F46] uppercase tracking-[0.15em] mb-2 px-3">
                  {group.title}
                </h3>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const hasAccess = canViewItem(item);
                  return (
                    <NavItem 
                      key={item.path} 
                      item={item} 
                      isActive={isActive(item.path)}
                      isCollapsed={isCollapsed}
                      onClick={handleNavClick}
                      notificationCount={getNotificationCount(item.path)}
                      disabled={!hasAccess}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* View Public Site Link */}
      <div className="px-3 mb-2">
        <a 
          href="/" 
          target="_blank" 
          rel="noopener noreferrer"
          className={`
            flex items-center gap-2 px-3 py-2 text-xs text-[#52525B] hover:text-[#D4AF37] 
            border border-dashed border-[#27272A] hover:border-[#D4AF37]/30 rounded-md transition-all
            ${isCollapsed ? 'justify-center' : ''}
          `}
        >
          <ExternalLink size={14} />
          {!isCollapsed && <span>Ver sitio público</span>}
        </a>
      </div>

      {/* User & Logout */}
      <div className="p-3 border-t border-[#1A1A1A]">
        <Link 
          to="/admin/mi-perfil"
          onClick={() => setIsMobileOpen(false)}
          className={`flex items-center gap-3 px-3 py-2.5 mb-2 rounded-md hover:bg-white/5 transition-colors ${isCollapsed ? 'justify-center' : ''}`}
        >
          {/* Avatar with glow effect */}
          <div className="relative">
            <div className="absolute inset-0 bg-[#D4AF37]/30 blur-md rounded-full" />
            <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8860B] flex items-center justify-center text-black text-sm font-black flex-shrink-0">
              {user?.username?.charAt(0).toUpperCase() || 'A'}
            </div>
          </div>
          {!isCollapsed && (
            <div className="overflow-hidden">
              <p className="text-sm font-bold text-white truncate">{user?.full_name || user?.username}</p>
              <p className="text-[10px] text-[#D4AF37] font-mono uppercase tracking-wider truncate">
                {user?.is_owner ? 'OWNER' : user?.role || 'Admin'}
              </p>
            </div>
          )}
        </Link>
        
        <a
          href="/mi-cuenta"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="sidebar-my-account-btn"
          className={`
            flex items-center gap-3 px-3 py-2.5 rounded-md w-full
            text-[#52525B] hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-all duration-200
            ${isCollapsed ? 'justify-center' : ''}
          `}
        >
          <UserCircle size={18} className="flex-shrink-0" />
          {!isCollapsed && (
            <span className="text-sm font-medium">
              Mi cuenta
            </span>
          )}
        </a>
      </div>
    </div>
  );
});

SidebarContent.displayName = 'SidebarContent';

// Storage key constant for scroll position  
const SCROLL_STORAGE_KEY = 'adminSidebarScrollPos';

const AdminSidebarNew = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isOwner, hasPermission } = useAuth();
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();
  const navRef = useRef(null);
  
  // Refs for scroll management - these persist across re-renders
  const targetScrollRef = useRef(0); // The scroll position we want to maintain
  const isNavigatingRef = useRef(false); // Flag to ignore scroll events during navigation
  const navigationTimeoutRef = useRef(null); // Timeout to re-enable scroll saving
  
  const [notifications, setNotifications] = useState({
    pending_events: 0,
    pending_benefits: 0,
    pending_producers: 0,
    pending_partners: 0,
    pending_dj: 0,
    pending_press: 0,
    pending_quotes: 0,
    pending_strikes: 0,
    pending_cancellations: 0,
    pending_withdrawals: 0,
    pending_claims: 0
  });

  // Load notification counts
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get('/settings/admin/notifications/counts');
        if (response.data) {
          setNotifications(response.data);
        }
      } catch (error) {
        console.log('Could not fetch notification counts');
      }
    };
    
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // Initialize targetScrollRef from sessionStorage on mount
  useEffect(() => {
    const saved = sessionStorage.getItem(SCROLL_STORAGE_KEY);
    if (saved) {
      const scrollPos = parseInt(saved, 10);
      if (!isNaN(scrollPos) && scrollPos > 0) {
        targetScrollRef.current = scrollPos;
      }
    }
  }, []);

  // Scroll event handler - only saves when NOT navigating
  const handleScroll = useCallback(() => {
    // Skip saving during navigation to prevent overwriting with 0
    if (isNavigatingRef.current) {
      return;
    }
    
    const navElement = document.querySelector('#admin-sidebar-nav');
    if (navElement) {
      const scrollPos = navElement.scrollTop;
      // Only save meaningful scroll positions (> 5px to avoid noise)
      if (scrollPos > 5) {
        targetScrollRef.current = scrollPos;
        sessionStorage.setItem(SCROLL_STORAGE_KEY, scrollPos.toString());
      }
    }
  }, []);

  // Attach scroll listener
  useEffect(() => {
    const navElement = document.querySelector('#admin-sidebar-nav');
    if (navElement) {
      navElement.addEventListener('scroll', handleScroll, { passive: true });
      return () => navElement.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Restore scroll position after route change
  useEffect(() => {
    const restoreScroll = () => {
      const navElement = document.querySelector('#admin-sidebar-nav');
      if (navElement && targetScrollRef.current > 0) {
        navElement.scrollTop = targetScrollRef.current;
      }
    };
    
    // Restore immediately and after DOM updates
    restoreScroll();
    requestAnimationFrame(restoreScroll);
    
    // Re-enable scroll saving after restoration completes
    const enableScrollSaving = setTimeout(() => {
      isNavigatingRef.current = false;
    }, 300);
    
    return () => {
      clearTimeout(enableScrollSaving);
    };
  }, [location.pathname]);

  // Called before navigation to preserve scroll position
  const saveScrollPosition = useCallback(() => {
    const navElement = document.querySelector('#admin-sidebar-nav');
    if (navElement) {
      const scrollPos = navElement.scrollTop;
      if (scrollPos > 0) {
        targetScrollRef.current = scrollPos;
        sessionStorage.setItem(SCROLL_STORAGE_KEY, scrollPos.toString());
      }
    }
    // Set navigating flag to prevent scroll listener from overwriting
    isNavigatingRef.current = true;
    
    // Clear any pending timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
  }, []);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const sidebarContentProps = {
    isCollapsed,
    setIsCollapsed,
    setIsMobileOpen,
    user,
    isOwner,
    hasPermission,
    handleLogout,
    isActive,
    saveScrollPosition,
    navRef,
    notifications
  };

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        data-testid="mobile-menu-btn"
        className="lg:hidden fixed top-4 left-4 z-50 p-2.5 bg-[#121212] border border-[#27272A] rounded-lg text-white hover:border-[#D4AF37]/50 transition-colors"
      >
        <Menu size={22} />
      </button>

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 bg-black/80 backdrop-blur-sm z-40"
            onClick={() => setIsMobileOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="lg:hidden fixed left-0 top-0 bottom-0 w-72 bg-[#0A0A0A] border-r border-[#1A1A1A] z-50 shadow-2xl shadow-black/50"
          >
            <button
              onClick={() => setIsMobileOpen(false)}
              data-testid="mobile-menu-close-btn"
              className="absolute top-4 right-4 p-2 text-[#52525B] hover:text-[#D4AF37] transition-colors"
            >
              <X size={20} />
            </button>
            <SidebarContent {...sidebarContentProps} />
          </motion.aside>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: isCollapsed ? 80 : 280 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="hidden lg:block fixed left-0 top-0 bottom-0 bg-[#0A0A0A] border-r border-[#1A1A1A] z-30 shadow-xl shadow-black/30"
      >
        <SidebarContent {...sidebarContentProps} />
      </motion.aside>

      {/* Spacer for main content */}
      <div 
        className={`hidden lg:block flex-shrink-0 transition-all duration-300`}
        style={{ width: isCollapsed ? 80 : 280 }}
      />
    </>
  );
};

export default AdminSidebarNew;
